// Real endpoints for the pre-call form.
// (Filename retained as `mockCallApi` from when these were stubs; safe to rename later.)
import apiClientCalls from "@/shared/api/apiClientCalls";
import apiClientBridge from "@/shared/api/apiClientBridge";
import {
  getNotaryListForSelection,
  getOrganizationList,
} from "@/entities/session";

// Per-queue overrides for the attorney IIN field label.
// Backend doesn't return these; we keep them client-side keyed by queue code.
const ATTORNEY_IIN_LABELS = {
  poa: {
    ru: "ИИН доверенного лица / поверенного",
    kz: "Сенім білдірілген тұлғаның ЖСН",
  },
  agreement: {
    ru: "ИИН лица, дающего согласие",
    kz: "Келісім беруші тұлғаның ЖСН",
  },
};

const QUEUE_TYPES_FALLBACK = [
  {
    code: "poa",
    label: "Доверенность",
    label_kz: "Сенімхат",
    required_fields: ["attorney_iin"],
  },
  {
    code: "agreement",
    label: "Согласие",
    label_kz: "Келісім",
    required_fields: ["attorney_iin"],
  },
  {
    code: "bachelor",
    label: "Заявление об отсутствии брака",
    label_kz: "Некенің жоқтығы туралы өтініш",
    required_fields: [],
  },
  {
    code: "consultation",
    label: "Консультация",
    label_kz: "Кеңес",
    required_fields: [],
  },
].map((q) => ({
  ...q,
  attorney_iin_label: ATTORNEY_IIN_LABELS[q.code]?.ru,
  attorney_iin_label_kz: ATTORNEY_IIN_LABELS[q.code]?.kz,
}));

// GET https://notary.q19.kz/cc/v1/calls/services
// Response: { items: [{ value, title_ru, title_kk, title_en, required_fields }] }
export async function fetchQueueTypes() {
  try {
    const { data } = await apiClientCalls.get("/services");
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return QUEUE_TYPES_FALLBACK;
    return items.map((q) => {
      const code = q.value || q.code;
      return {
        code,
        label: q.title_ru || q.name || q.label || code,
        label_kz: q.title_kk || q.name_kz || q.label_kz || q.title_ru || code,
        required_fields: Array.isArray(q.required_fields)
          ? q.required_fields
          : [],
        attorney_iin_label: ATTORNEY_IIN_LABELS[code]?.ru,
        attorney_iin_label_kz: ATTORNEY_IIN_LABELS[code]?.kz,
      };
    });
  } catch {
    return QUEUE_TYPES_FALLBACK;
  }
}

export async function fetchOrganizations() {
  const items = await getOrganizationList();
  return items.map((o) => ({
    id: o.id,
    code: o.code,
    label: o.short_name_ru || o.name || o.code,
    label_kz:
      o.short_name_kz || o.name_kz || o.short_name_ru || o.name || o.code,
  }));
}

export async function fetchNotaries(organizationId) {
  if (organizationId == null) return [];
  const data = await getNotaryListForSelection({
    page: 1,
    page_size: 100,
    organization_id: organizationId,
  });
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map((n) => ({
    uuid: n.uuid,
    identifier: n.identifier,
    label:
      [n.last_name, n.first_name, n.middle_name].filter(Boolean).join(" ") ||
      n.identifier ||
      n.uuid,
    is_online: n.is_online === true,
    raw: n,
  }));
}

// POST https://inqbox.q19.kz/bridge/api/v1/staff/employees/lookup
// Body: { external_ids: [notaryIdentifier] }
// Returns the operator id to be sent to the call iframe + request body.
export async function lookupTargetOperatorId(notaryIdentifier) {
  if (!notaryIdentifier) return null;
  const { data } = await apiClientBridge.post("/staff/employees/lookup", {
    external_ids: [String(notaryIdentifier)],
  });
  // Response shape is not fully known — accept array, items array, or first object.
  const list = Array.isArray(data)
    ? data
    : data?.items || data?.employees || [];
  const employee = list[0] || (typeof data === "object" ? data : null);
  return (
    employee?.id ||
    employee?.user_id ||
    employee?.operator_id ||
    employee?.target_operator_id ||
    null
  );
}

// POST https://notary.q19.kz/cc/v1/calls/requests
export async function submitCallRequest(payload) {
  const { data } = await apiClientCalls.post("/requests", payload);
  return data;
}

// GET https://notary.q19.kz/cc/v1/calls/validate/iin?value=<iin>
// 200 -> { valid, message, birthdate, sex }
// 422 -> { detail: [{ msg, ... }] }
export async function validateIin(iin) {
  const { data } = await apiClientCalls.get("/validate/iin", {
    params: { value: iin },
  });
  return data;
}
